import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Loader2, Hourglass, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import {
  convertTextToHtml,
  ensureDisclaimerAtEnd,
  removeCitationMarkers,
  removeSeparatorLines,
} from "./DebateUtils";

const DebateSummary = ({
  summary,
  loading,
  debating,
  isOpen,
  onToggle,
  handleOpenEditor,
  darkMode,
  title,
  isLocked = false,
}) => {
  const streamingConsensusRef = useRef(null);

  // Auto-scroll to bottom of streaming summary
  useEffect(() => {
    if (streamingConsensusRef.current && (debating || loading) && isOpen) {
      streamingConsensusRef.current.scrollTop =
        streamingConsensusRef.current.scrollHeight;
    }
  }, [summary, debating, loading, isOpen]);

  const hasContent = summary && summary.length > 0;

  return (
    <div className="debate-round-container debate-summary-container">
      <button
        onClick={onToggle}
        className="debate-round-header debate-summary-header"
        style={{
          background: darkMode
            ? "linear-gradient(to right, #1e3a8a, #1e40af)"
            : "linear-gradient(to right, #eff6ff, #dbeafe)",
          border: darkMode ? "1px solid #1e40af" : "1px solid #bfdbfe",
        }}
      >
        <span
          className="debate-round-title"
          style={{
            color: darkMode ? "#e0e7ff" : "#1e40af",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>{title || "Final Consensus & Summary"}</span>
          {loading && (
            <Hourglass size={16} className="debate-hourglass-queued" aria-hidden />
          )}
        </span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="debate-round-content"
            style={{
              borderLeft: darkMode ? "4px solid #3b82f6" : "4px solid #60a5fa",
              backgroundColor: darkMode ? "#0f172a" : "#ffffff",
            }}
          >
            <div className="debate-summary-inner" style={{ padding: "1.5rem" }}>
              <h3
                style={{
                  marginTop: 0,
                  color: darkMode ? "#e2e8f0" : "#334155",
                  marginBottom: "1rem",
                }}
              >
                Claude Final Consensus
              </h3>

              {isLocked ? (
                <div>
                  <div
                    style={{
                      borderRadius: "0.75rem",
                      overflow: "hidden",
                      border: darkMode ? "1px solid #334155" : "1px solid #e2e8f0",
                      background: darkMode ? "#0b1220" : "#f8fafc",
                    }}
                  >
                    <div
                      className="response-html"
                      style={{
                        maxHeight: "440px",
                        overflow: "hidden",
                        filter: "blur(5px)",
                        opacity: 0.78,
                        padding: "1rem",
                      }}
                    >
                      <p><strong>I. Issue Presented</strong></p>
                      <p>This summary is available after creating an account and upgrading access.</p>
                      <p><strong>II. Arguments</strong></p>
                      <p>Key points from each model are synthesized into a final legal position.</p>
                      <p><strong>III. Recommendation</strong></p>
                      <p>Actionable next steps and risk considerations are included here.</p>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "520px",
                        textAlign: "center",
                        padding: "1rem 1rem 1.25rem",
                        margin: "0 auto",
                      }}
                    >
                        <div
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "999px",
                            border: darkMode
                              ? "1px solid #475569"
                              : "1px solid #cbd5e1",
                            background: darkMode ? "rgba(15,23,42,0.8)" : "#f8fafc",
                            color: darkMode ? "#cbd5e1" : "#64748b",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 0.85rem",
                          }}
                        >
                          <Lock size={15} />
                        </div>
                        <div
                          style={{
                            fontSize: "2rem",
                            fontWeight: 700,
                            color: darkMode ? "#f8fafc" : "#111827",
                            lineHeight: 1.2,
                            marginBottom: "0.45rem",
                          }}
                        >
                          This summary determines where your argument is most vulnerable
                        </div>
                        <div
                          style={{
                            fontSize: "1.65rem",
                            color: darkMode ? "#d1d5db" : "#374151",
                            lineHeight: 1.35,
                            marginBottom: "1rem",
                          }}
                        >
                          See exactly where opposing counsel would attack - and how to fix it before you file.
                        </div>
                        <Link
                          to="/pricing"
                          style={{
                            display: "inline-block",
                            minWidth: "210px",
                            padding: "0.78rem 1.3rem",
                            borderRadius: "0.65rem",
                            textDecoration: "none",
                            background: "#3b5bfd",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "1.05rem",
                          }}
                        >
                          Start Free Trial
                        </Link>
                        <div
                          style={{
                            marginTop: "0.55rem",
                            fontSize: "0.95rem",
                            color: darkMode ? "#9ca3af" : "#6b7280",
                          }}
                        >
                          7-day free trial. Cancel anytime.
                        </div>
                    </div>
                  </div>
                </div>
              ) : hasContent ? (
                <>
                  <div
                    ref={streamingConsensusRef}
                    className="response-html"
                    style={{
                      maxHeight: "600px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: convertTextToHtml(
                        ensureDisclaimerAtEnd(
                          removeCitationMarkers(removeSeparatorLines(summary)),
                          !loading && !debating,
                        ),
                      ),
                    }}
                  />
                  <div
                    className="response-actions"
                    style={{
                      marginTop: "1rem",
                      display: "flex",
                      gap: "0.5rem",
                    }}
                  >
                    <motion.button
                      className="response-btn"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() =>
                        handleOpenEditor(
                          "debate-consensus",
                          "Claude Final Consensus Summary",
                          convertTextToHtml(
                            ensureDisclaimerAtEnd(
                              removeCitationMarkers(
                                removeSeparatorLines(summary),
                              ),
                              !loading && !debating,
                            ),
                          ),
                          {},
                        )
                      }
                      disabled={debating || loading}
                      style={{
                        opacity: debating || loading ? 0.6 : 1,
                        cursor: debating || loading ? "not-allowed" : "pointer",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.5rem",
                        border: "none",
                        background: darkMode ? "#2563eb" : "#3b82f6",
                        color: "white",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Edit / Export
                    </motion.button>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: darkMode ? "#94a3b8" : "#64748b",
                    padding: "1rem",
                    fontStyle: "italic",
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="debate-spinner" />
                      <span>Generating summary...</span>
                    </>
                  ) : (
                    <span>Waiting for debate completion...</span>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DebateSummary;
